import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts'
import { Box, Typography } from '@mui/material'
import type { WalkWrapperRow } from '~/api/endpoints/walks'
import type { Facet } from '~/state/useChartKnobsState'
import type { Group } from './dataPipeline'
import { useStore } from '~/state/useStore'
import { formatAxisTick } from '~/util/numbers'
import { MESSAGES } from './chrome/chartChrome'
import { AXIS_TICK, GRID_STROKE, TOOLTIP_NUM_STYLE } from './chrome/chartStyle'

interface SeriesPoint {
  time: string
  [seriesKey: string]: string | number | undefined
}

interface Props {
  rows?: WalkWrapperRow[]
  points?: SeriesPoint[]
  groups?: Group[]
  facet?: Facet
  inactiveSeries?: string[]
  yDomain?: [number, number]
  hideYAxis?: boolean
}

export function pivot(rows: WalkWrapperRow[]): { points: SeriesPoint[]; seriesKeys: string[] } {
  const distinctKpis = new Set<string>()
  for (const r of rows) distinctKpis.add(r.kpi_type)
  const includeKpiInKey = distinctKpis.size > 1

  const seriesKeys = new Set<string>()
  const byTime = new Map<string, SeriesPoint>()
  for (const r of rows) {
    const dimPart = `${r.dim_name}=${r.dim_value}`
    const seriesKey = includeKpiInKey ? `${r.kpi_type}/${dimPart}` : dimPart
    seriesKeys.add(seriesKey)
    const existing = byTime.get(r.time) ?? { time: r.time }
    existing[seriesKey] = r.value
    byTime.set(r.time, existing)
  }
  const points = [...byTime.values()].sort((a, b) => String(a.time).localeCompare(String(b.time)))
  return { points, seriesKeys: [...seriesKeys] }
}

export function WrapperChart(props: Props) {
  const palette = useStore((s) => s.personal.chipPalette)
  const fallbackPivot = props.rows ? pivot(props.rows) : { points: [], seriesKeys: [] }
  const points = props.points ?? fallbackPivot.points
  const groups: Group[] =
    props.groups ??
    fallbackPivot.seriesKeys.map((k, i) => ({
      key: k,
      label: k,
      color: { hex: palette[i % palette.length] ?? '#000', opacity: 1 },
    }))
  const inactive = props.inactiveSeries ?? []
  const activeGroups = groups.filter((g) => !inactive.includes(g.key))

  if (points.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{MESSAGES.noDataForFilters}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', height: 360 }}>
      <ResponsiveContainer>
        <BarChart data={points}>
          <CartesianGrid stroke={GRID_STROKE} vertical={false} />
          <XAxis dataKey="time" tick={AXIS_TICK} />
          <YAxis
            hide={props.hideYAxis}
            domain={props.yDomain}
            tickFormatter={formatAxisTick}
            tick={AXIS_TICK}
          />
          <Tooltip contentStyle={TOOLTIP_NUM_STYLE} />
          {activeGroups.map((g) => (
            <Bar key={g.key} dataKey={g.key} fill={g.color.hex} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}
